/** Keys of MediaForm's platform-link fields, used to look up validation rules. */
export type PlatformKey =
  | 'website'
  | 'spotify'
  | 'soundcloud'
  | 'appleMusic'
  | 'amazonMusic'
  | 'youtubeMusic';

interface PlatformRule {
  label: string;
  /** Allowed hostnames (and their subdomains). Omitted for generic any-domain fields like website. */
  domains?: string[];
  /** Sample URL shown in the validation error so users know the expected format. */
  example: string;
}

const PLATFORM_RULES: Record<PlatformKey, PlatformRule> = {
  website: { label: 'website', example: 'https://example.com' },
  spotify: {
    label: 'Spotify',
    domains: ['spotify.com'],
    example: 'https://open.spotify.com/artist/...',
  },
  soundcloud: {
    label: 'SoundCloud',
    domains: ['soundcloud.com'],
    example: 'https://soundcloud.com/artist-name',
  },
  appleMusic: {
    label: 'Apple Music',
    domains: ['music.apple.com', 'itunes.apple.com'],
    example: 'https://music.apple.com/us/artist/artist-name/123456789',
  },
  amazonMusic: {
    label: 'Amazon Music',
    domains: ['music.amazon.com', 'amazon.com'],
    example: 'https://music.amazon.com/artists/...',
  },
  youtubeMusic: {
    label: 'YouTube Music',
    domains: ['music.youtube.com', 'youtube.com', 'youtu.be'],
    example: 'https://music.youtube.com/channel/...',
  },
};

/** Parses a URL and returns its lowercased, `www.`-stripped hostname, or null if malformed. */
function parseHostname(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (!/^https?:$/i.test(parsed.protocol)) return null;
    if (!parsed.hostname.includes('.')) return null;
    return parsed.hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
}

function hostnameMatchesDomain(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

/** Generic "is this a well-formed http(s) URL" check — no domain restriction. */
export function isValidUrl(url: string): boolean {
  return parseHostname(url) !== null;
}

/**
 * Validates that `url` is a well-formed URL whose hostname belongs to the given platform.
 * An empty string is always valid — required-ness is the caller's responsibility.
 */
export function validatePlatformUrl(platform: PlatformKey, url: string): boolean {
  if (!url.trim()) return true;

  const hostname = parseHostname(url);
  if (!hostname) return false;

  const { domains } = PLATFORM_RULES[platform];
  if (!domains) return true;

  return domains.some((domain) => hostnameMatchesDomain(hostname, domain));
}

export function getPlatformUrlError(platform: PlatformKey): string {
  const { label, example } = PLATFORM_RULES[platform];
  return `Please enter a valid ${label} URL, e.g. ${example}`;
}
